<template>
  <div class="tab-page-root bg-surface text-on-surface">
    <div ref="scrollContainer" class="tab-page-scroll pb-24">
    <header class="sticky top-0 w-full z-50 bg-surface dark:bg-surface-dim shadow-sm flex items-center justify-between px-container-margin py-base gap-gutter">
      <div>
        <h1 class="font-headline-sm text-headline-sm font-bold text-primary">圈子</h1>
        <p class="font-label-md text-label-md text-on-surface-variant">{{ pageSubtitle }}</p>
      </div>
    </header>

    <main class="max-w-screen-md mx-auto">
      <section class="pt-stack-md">
        <div v-if="showMissingCirclePrompt" class="px-container-margin mb-stack-sm">
          <div class="missing-circle-panel">
            <span class="material-symbols-outlined text-primary text-[32px]">hub</span>
            <div class="min-w-0">
              <h2 class="font-headline-sm text-headline-sm text-on-surface">推荐设置学校或圈子</h2>
              <p class="font-body-md text-body-md text-on-surface-variant mt-1">也可先浏览「全英国」全部帖子。</p>
            </div>
            <button
              class="missing-circle-action"
              type="button"
              @click="goToSettings"
            >去设置</button>
          </div>
        </div>

        <div class="category-scroll hide-scrollbar">
          <button
            class="category-chip"
            :class="{ 'is-active': selectedCircleId === 0 }"
            type="button"
            @click="selectCircle({ id: 0, title: '全英国' })"
          >全英国</button>
          <button
            v-for="circle in accountCircles"
            :key="'circle-' + circle.id"
            class="category-chip"
            :class="{ 'is-active': selectedCircleId === Number(circle.id) }"
            type="button"
            @click="selectCircle(circle)"
          >{{ circle.title || circle.momo || '未命名圈子' }}</button>
        </div>
      </section>

      <section v-if="canLoadGoods" class="pt-stack-sm">
        <div class="category-scroll hide-scrollbar">
          <button
            v-for="cat in categories"
            :key="cat.id"
            class="category-chip"
            :class="{ 'is-active': activeCategoryId === cat.id }"
            type="button"
            @click="selectCategory(cat.id)"
          >{{ cat.label }}</button>
        </div>
      </section>

      <div v-if="loading && items.length === 0 && canLoadGoods" class="px-container-margin py-stack-lg">
        <div class="bg-surface-container-lowest border border-surface-container rounded-xl p-stack-lg text-center text-on-surface-variant">
          <span class="material-symbols-outlined text-[32px] animate-pulse">progress_activity</span>
          <p class="font-body-md text-body-md mt-base">正在加载圈子商品...</p>
        </div>
      </div>

      <div v-if="errorMessage" class="px-container-margin py-stack-md" role="alert">
        <div class="bg-error-container/20 border border-error-container rounded-xl p-stack-md text-error font-body-md text-body-md">
          <p>{{ errorMessage }}</p>
          <button
            v-if="needLogin"
            class="mt-3 w-full h-10 rounded-full bg-primary text-on-primary font-label-md text-label-md font-bold active:scale-[0.98]"
            type="button"
            @click="goLogin"
          >去登录</button>
        </div>
      </div>

      <div v-show="canLoadGoods && !(loading && items.length === 0)" class="px-container-margin grid grid-cols-1 gap-stack-md pt-stack-md">
        <div v-if="items.length === 0 && !loading" class="bg-surface-container-lowest border border-surface-container rounded-xl p-stack-lg text-center">
          <span class="material-symbols-outlined text-[40px] text-on-surface-variant">inventory_2</span>
          <p class="font-headline-sm text-headline-sm text-on-surface mt-base">{{ selectedCircleId === 0 ? '暂时没有商品' : '该圈子暂时没有商品' }}</p>
          <p class="font-body-md text-body-md text-on-surface-variant mt-1">换个圈子或商品分类再看看。</p>
        </div>

        <a
          v-for="item in items"
          :key="item.id"
          class="bg-surface-container-lowest rounded-xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-surface-container flex active:scale-[0.98] transition-transform"
          href="javascript:void(0)"
          @click="goToProduct(item.id)"
        >
          <div class="w-1/3 aspect-square relative bg-surface-container">
            <img class="w-full h-full object-cover" :src="normalizeImage(item.image || item.img || item.cover)" alt="" />
            <div v-if="item.circle_title || item.momo" class="absolute top-2 left-2 px-2 py-1 bg-surface/90 glass-effect rounded-lg">
              <span class="font-label-md text-[10px] font-bold text-primary">{{ item.circle_title || item.momo }}</span>
            </div>
          </div>
          <div class="p-stack-md flex-1 flex flex-col justify-between min-w-0">
            <div>
              <h3 class="font-body-md text-body-md text-on-surface line-clamp-2 mb-1">{{ item.title || '未命名商品' }}</h3>
              <p class="font-price-display text-price-display text-primary">{{ formatPrice(item.price) }}</p>
            </div>
            <div class="flex items-center justify-between gap-base mt-base">
              <span class="font-label-md text-label-md text-on-surface-variant truncate">{{ item.place || item.city || item.circle_title || '圈子' }}</span>
              <span class="font-label-md text-label-md text-on-surface-variant whitespace-nowrap">{{ item.status_text || item.review_text || '' }}</span>
            </div>
          </div>
        </a>
      </div>

      <div v-if="items.length > 0 && hasMore && canLoadGoods" class="px-container-margin py-stack-md">
        <button
          class="w-full h-11 rounded-full bg-surface-container text-on-surface font-label-md text-label-md border border-outline-variant/30 active:scale-95 transition-all disabled:opacity-60 disabled:pointer-events-none"
          type="button"
          :disabled="loading"
          @click="loadMore"
        >{{ loading ? '加载中...' : '加载更多' }}</button>
      </div>
    </main>

    <div
      class="fixed bottom-10 left-1/2 bg-on-surface text-surface px-6 py-3 rounded-full shadow-2xl z-[100] pointer-events-none"
      :class="toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
      style="transform: translateX(-50%); transition: opacity 0.3s, transform 0.3s;"
    >
      <div class="flex items-center gap-2">
        <span class="material-symbols-outlined text-[20px]">info</span>
        <span class="font-label-md text-label-md">{{ toastMessage }}</span>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import getApi from '@/api';
import store from '@/store';

const api = getApi();
const API_IMAGE_BASE = 'https://api.pandacampus.com';
const FALLBACK_IMAGE = require('@/assets/images/4f95740433e8.jpg');

export default {
  name: 'NearbyPage',
  data() {
    return {
      selectedCircleId: Number(sessionStorage.getItem('nearbyFilterCircleId') || 0),
      activeCategoryId: Number(sessionStorage.getItem('nearbyCategoryId') || 0),
      page: 1,
      limit: 10,
      accountCircles: [],
      categories: [{ id: 0, label: '全部' }],
      items: [],
      hasMore: false,
      loading: false,
      loadedKey: '',
      pendingKey: '',
      requestId: 0,
      errorMessage: '',
      needLogin: false,
      pageReady: false,
      toastVisible: false,
      toastMessage: '',
      toastTimer: null
    };
  },
  computed: {
    hasCircles() {
      return this.accountCircles.length > 0;
    },
    activeCircleName() {
      if (this.selectedCircleId === 0) return '全英国';
      const circle = this.accountCircles.find((item) => Number(item.id) === this.selectedCircleId);
      if (!circle) return '';
      return circle.title || circle.momo || circle.city || '';
    },
    showMissingCirclePrompt() {
      return !this.hasCircles;
    },
    canLoadGoods() {
      return this.selectedCircleId === 0 || (this.hasCircles && this.selectedCircleId > 0);
    },
    pageSubtitle() {
      return this.activeCircleName || '选择圈子查看商品';
    }
  },
  methods: {
    normalizeCircles(list) {
      if (!Array.isArray(list)) return [];
      return list.filter(function (circle) {
        return circle && circle.id != null;
      }).map(function (circle) {
        return {
          id: Number(circle.id) || 0,
          title: circle.title || '',
          momo: circle.momo || '',
          city: circle.city || '',
          contry: circle.contry || circle.country || ''
        };
      });
    },

    showToast(message) {
      this.toastMessage = message || '';
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2500);
    },

    goToSettings() {
      if (!store.state.token) {
        this.showToast('未登录，请先登录');
        return;
      }
      this.$router.push({ name: 'edit-profile', query: { from: 'nearby' } });
    },

    ensureSelectedCircle() {
      if (!this.accountCircles.length) {
        this.selectedCircleId = 0;
        return;
      }

      if (this.selectedCircleId === 0) return;

      const exists = this.accountCircles.some((circle) => Number(circle.id) === this.selectedCircleId);
      if (!exists) {
        this.selectCircle({ id: 0, title: '全英国' }, { reload: false });
      }
    },

    selectCircle(circle, options) {
      options = options || {};
      const id = Number(circle && circle.id) || 0;
      if (this.selectedCircleId === id && options.reload !== true) return;

      this.selectedCircleId = id;
      sessionStorage.setItem('nearbyFilterCircleId', String(id));
      this.page = 1;
      this.loadedKey = '';

      if (options.reload !== false) {
        this.loadNearbyGoods({ reset: true, force: true });
      }
    },

    loadAccountCircles() {
      const cached = this.normalizeCircles(store.state.userinfo && store.state.userinfo.circle_list);
      this.accountCircles = cached;
      this.ensureSelectedCircle();

      if (!store.state.token) {
        return Promise.resolve(cached);
      }

      return api.userInfo(store.state.token)
        .then((profile) => {
          store.setUserinfo(profile || {});
          this.accountCircles = this.normalizeCircles(profile && profile.circle_list);
          this.ensureSelectedCircle();
          return this.accountCircles;
        })
        .catch(() => {
          this.ensureSelectedCircle();
          return cached;
        });
    },

    loadCategories() {
      return api.categories(store.state.token)
        .then((list) => {
          this.categories = [{ id: 0, label: '全部' }].concat(
            (list || []).map(function (cat) {
              return {
                id: Number(cat.id) || 0,
                label: cat.title || ''
              };
            }).filter(function (cat) {
              return cat.id !== 0 || cat.label;
            })
          );
        })
        .catch(() => {
          this.categories = [{ id: 0, label: '全部' }];
        });
    },

    selectCategory(id) {
      if (this.activeCategoryId === id) return;
      this.activeCategoryId = id;
      sessionStorage.setItem('nearbyCategoryId', String(id));
      this.page = 1;
      this.loadedKey = '';
      this.loadNearbyGoods({ reset: true, force: true });
    },

    makeLoadKey(page) {
      return [
        this.selectedCircleId,
        this.activeCategoryId,
        page
      ].join('|');
    },

    normalizeImage(src) {
      if (!src) return FALLBACK_IMAGE;
      if (/^https?:\/\//i.test(src)) return src;
      if (src.charAt(0) === '/') return API_IMAGE_BASE + src;
      return src;
    },

    formatPrice(price) {
      if (price === null || price === undefined || price === '') return '£0.00';
      const num = Number(price);
      if (Number.isFinite(num)) return '£' + num.toFixed(2);
      return '£' + price;
    },

    goLogin() {
      this.$router.push({ name: 'login', query: { redirect: 'nearby' } });
    },

    clearError() {
      this.errorMessage = '';
      this.needLogin = false;
    },

    showError(message, options) {
      options = options || {};
      this.needLogin = !!options.needLogin;
      this.errorMessage = message || (this.needLogin ? '需要登录后才能查看圈子商品' : '圈子商品加载失败，请稍后再试。');
    },

    isAuthError(error) {
      if (!error) return false;
      if (error.status === 401 || error.code === 401) return true;
      var msg = String(error.message || '');
      return /401|登录|未登录|请先登录|需要登录/.test(msg);
    },

    loadNearbyGoods(options) {
      options = options || {};

      if (!this.canLoadGoods) {
        this.items = [];
        this.hasMore = false;
        this.loadedKey = '';
        this.pendingKey = '';
        this.clearError();
        return Promise.resolve();
      }

      const reset = options.reset !== false;
      const page = reset ? 1 : this.page;
      const key = this.makeLoadKey(page);
      if (!options.force && (key === this.loadedKey || key === this.pendingKey)) {
        return Promise.resolve();
      }

      this.pendingKey = key;
      this.loading = true;
      this.requestId += 1;
      this.clearError();

      const requestId = this.requestId;
      return api.nearList({
        range: 1,
        limit: this.limit,
        page: page,
        circle_id: this.selectedCircleId,
        type_id: this.activeCategoryId
      }, store.state.token)
        .then(data => {
          if (requestId !== this.requestId) return;

          const rows = Array.isArray(data.data) ? data.data : [];
          this.items = reset ? rows : this.items.concat(rows);
          this.page = Number(data.current_page || page);
          const lastPage = Number(data.last_page || 0);
          this.hasMore = lastPage ? this.page < lastPage : rows.length >= this.limit;
          this.loadedKey = key;
        })
        .catch(error => {
          if (requestId !== this.requestId) return;
          if (reset) {
            this.items = [];
            this.hasMore = false;
          }
          if (this.isAuthError(error)) {
            this.showError('需要登录后才能查看圈子商品', { needLogin: true });
            return;
          }
          this.showError(error.message || '圈子商品加载失败，请稍后再试。');
        })
        .finally(() => {
          if (requestId !== this.requestId) return;
          this.loading = false;
          this.pendingKey = '';
        });
    },

    loadMore() {
      if (this.loading || !this.hasMore) return;
      this.page += 1;
      this.loadNearbyGoods({ reset: false, force: true });
    },

    goToProduct(id) {
      if (id != null) {
        store.setActiveGoodsId(String(id));
      }
      this.$router.push({ name: 'product-detail' });
    },

    refreshNearbyPage() {
      return Promise.all([
        this.loadAccountCircles(),
        this.loadCategories()
      ]).then(() => {
        return this.loadNearbyGoods({ reset: true, force: true });
      });
    }
  },

  mounted() {
    this.refreshNearbyPage();
    this.$nextTick(() => {
      this.pageReady = true;
    });
  },

  beforeDestroy() {
    clearTimeout(this.toastTimer);
  }
};
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  display: inline-block;
  line-height: 1;
  text-transform: none;
  letter-spacing: normal;
  word-wrap: normal;
  white-space: nowrap;
  direction: ltr;
}
body {
  font-family: 'Inter', sans-serif;
  -webkit-tap-highlight-color: transparent;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.glass-effect {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.category-scroll {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  padding: 0 16px 4px;
}
.category-chip {
  min-width: 54px;
  height: 32px;
  padding: 0 14px;
  border-radius: 9999px;
  border: 1px solid rgba(121, 116, 126, 0.2);
  white-space: nowrap;
  color: var(--md-sys-color-on-surface-variant, #49454f);
  background: var(--md-sys-color-surface-container-lowest, #fff);
}
.category-chip.is-active {
  color: var(--md-sys-color-on-primary-container, #21005d);
  background: var(--md-sys-color-primary-container, #eaddff);
  border-color: transparent;
}
.missing-circle-panel {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  border-radius: 12px;
  border: 1px solid rgba(103, 80, 164, 0.18);
  background: rgba(103, 80, 164, 0.08);
  padding: 14px;
}
.missing-circle-action {
  grid-column: 2;
  justify-self: start;
  margin-top: 2px;
  height: 34px;
  padding: 0 16px;
  border-radius: 9999px;
  background: var(--md-sys-color-primary, #6750a4);
  color: var(--md-sys-color-on-primary, #fff);
  font-size: 13px;
  font-weight: 700;
}
</style>
